import {
  GroupedPlayersData,
  HourlySweep,
  HourlySweepData,
  HourlySweepStatus,
  HourlySweepStatusColors,
  LegendData,
} from './dashboard.type';
import styles from './dashboard.module.css';
import warning from '../../assets/warning.png';
import { PolarAngleAxis, RadialBar, RadialBarChart } from 'recharts';
import user from '../../assets/user.png';
import { Trans } from 'react-i18next';
import { useEffect, useState } from 'react';
import ClosedEyeIcon from '../../assets/exportableIcons/closedEyeIcon';
import EyeIcon from '../../assets/exportableIcons/eyeIcon';
import { DEFAULT_HOURLY_SWEEP_MACROS } from '../../utils/constant';
import moment, { Moment } from 'moment';
import { Sweep, SweepStatus, SweepType } from '@inplay/models';
import { useOrganizationContext } from '../../service/context/organizationContext';

export const RenderLegendContent = () => {
  const legendData: LegendData[] = [
    {
      icon: <img src={warning} alt="" style={{ height: '18px' }} />,
      text: <Trans i18nKey={'dashboardNew.excluded'} />,
    },
    {
      icon: <div className={`${styles.dot} ${styles.red} h-[16px]`} />,
      text: <Trans i18nKey={'dashboardNew.excluded'} />,
    },
    {
      icon: <div className={`${styles.dot} ${styles.orange} h-[16px]`} />,
      text: <Trans i18nKey={'dashboardNew.intervention'} />,
    },
    {
      icon: <div className={`${styles.dot} ${styles.lime} h-[16px]`} />,
      text: <Trans i18nKey={'dashboardNew.interventionOffer'} />,
    },
    {
      icon: <div className={`${styles.dot} ${styles.green} h-[16px]`} />,
      text: <Trans i18nKey={'dashboardNew.lowRisk'} />,
    },
    {
      icon: <div className={`${styles.interventionDot}`}>#</div>,
      text: <Trans i18nKey={'dashboardNew.totalIntervention'} />,
    },
    {
      icon: <div className={`${styles.pill}`}>00 m</div>,
      text: <Trans i18nKey={'dashboardNew.timeRemaining'} />,
    },
    {
      icon: <div className={`${styles.pill} ${styles.yellow}`}>00 m</div>,
      text: <Trans i18nKey={'dashboardNew.timeElapsed'} />,
    },
  ];
  return (
    <div className={styles.legend}>
      {legendData.map((item: LegendData, index: number) => (
        <div key={index} className={styles.legendItem}>
          <span className={styles.legendIcon}>{item.icon}</span>
          <span className={styles.legendText}>{item.text}</span>
        </div>
      ))}
    </div>
  );
};

export const RenderSummaryContent = (playerData: GroupedPlayersData) => {
  const totalPlayers =
    playerData.low.length +
    playerData.intervention_offered.length +
    playerData.intervention_required.length +
    playerData.excluded.length;

  const dataset = [
    {
      key: 'outOfRisk',
      value: playerData.low.length,
      color: '#51F28B',
      innerRadius: '90%',
      outerRadius: '100%',
    },
    {
      key: 'interventionOffered',
      value: playerData.intervention_offered.length,
      color: '#D5FC32',
      innerRadius: '72%',
      outerRadius: '82%',
    },
    {
      key: 'requiresIntervention',
      value: playerData.intervention_required.length,
      color: '#FFA81D',
      innerRadius: '54%',
      outerRadius: '64%',
    },
    {
      key: 'excluded',
      value: playerData.excluded.length,
      color: '#F45B5B',
      innerRadius: '36%',
      outerRadius: '46%',
    },
  ];

  return (
    <>
      <div className={styles.summaryChart}>
        <div className={styles.centerIcon}>
          <img src={user} alt="" />
        </div>
        {dataset.map((item) => {
          const percent = (item.value / totalPlayers) * 100;

          const data = [{ name: 'filled', value: percent }];

          return (
            <RadialBarChart
              key={item.key}
              width={200}
              height={200}
              cx="50%"
              cy="50%"
              innerRadius={item.innerRadius}
              outerRadius={item.outerRadius}
              data={data}
              startAngle={90}
              endAngle={-270}
              className={styles.radialLayer}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar
                dataKey="value"
                background={{ fill: '#F0EFFA' }}
                fill={item.color}
                cornerRadius={10}
              />
            </RadialBarChart>
          );
        })}
      </div>
      <div className={styles.summaryStats}>
        <div className={styles.statItem}>
          <div className={styles.label}>
            <div className={`${styles.dot} ${styles.red} h-[10px]`} />
            <Trans i18nKey={'dashboardNew.excluded'} />
          </div>
          <div className={styles.value}>{playerData.excluded.length}</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.label}>
            <div className={`${styles.dot} ${styles.orange} h-[10px]`} />
            <Trans i18nKey={'dashboardNew.intervention'} />
          </div>
          <div className={styles.value}>
            {playerData.intervention_required.length}
          </div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.label}>
            <div className={`${styles.dot} ${styles.lime} h-[10px]`} />
            <Trans i18nKey={'dashboardNew.interventionOffer'} />
          </div>
          <div className={styles.value}>
            {playerData.intervention_offered.length}
          </div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.label}>
            <div className={`${styles.dot} ${styles.green} h-[10px]`} />
            <Trans i18nKey={'dashboardNew.lowRisk'} />
          </div>
          <div className={styles.value}>{playerData.low.length}</div>
        </div>
      </div>
      <div className={styles.divider} />
      <div className={styles.statItem}>
        <div className={`${styles.label} font-medium`}>
          <Trans i18nKey={'dashboardNew.totalPlayers'} />
        </div>
        <div className={styles.value}>{totalPlayers}</div>
      </div>
    </>
  );
};

export const IconTooltip = ({
  text,
  className,
}: {
  text: string;
  className?: string;
}) => (
  <div className={`${styles.tooltip} ${className} bg-TOOLTIP_THEME_COLOR`}>
    <Trans i18nKey={text} />
  </div>
);

const getCurrentHourRange = (): string => {
  const start: Moment = moment().startOf('hour');
  const end: Moment = moment(start).add(1, 'hour');
  return `${start.format('hh:00 A')} - ${end.format('hh:00 A')}`;
};

const EyeCard = ({ data, type }: { data: HourlySweep; type: SweepType }) => {
  const backgroundColor: string =
    type === SweepType.Voluntary
      ? HourlySweepStatusColors.VOLUNTARY
      : data?.status === HourlySweepStatus.COMPLETED
        ? HourlySweepStatusColors.COMPLETED
        : data?.status === HourlySweepStatus.PENDING
          ? HourlySweepStatusColors.PENDING
          : data?.status === HourlySweepStatus.MISSED
            ? HourlySweepStatusColors.MISSED
            : '#F4F4F4';
  return (
    <div
      className={`${styles.eyeCard}`}
      style={{
        color: '#1A1A1A',
        background: backgroundColor,
      }}
    >
      {data && (
        <>
          {data.status === HourlySweepStatus.COMPLETED ? (
            <EyeIcon />
          ) : (
            <ClosedEyeIcon stroke={'#1A1A1A'} />
          )}
          <span>
            {data && type === SweepType.Voluntary
              ? moment(data.sweepTime).format('HH:mm')
              : data.sweepSchedule}
          </span>
        </>
      )}
    </div>
  );
};

export const RenderHourlySweepContent = (sweepsSummary: Sweep[]) => {
  const [hourlySweepData, setHourlySweepData] = useState<HourlySweepData>();
  const [timeRangeString, setTimeRangeString] = useState<string>('');
  const { selectedVenueBranch } = useOrganizationContext();

  useEffect(() => {
    setHourlySweepData(
      processSweepsSummary(
        sweepsSummary,
        selectedVenueBranch?.sweepIntervalInMinutes || 20,
      ),
    );
    setTimeRangeString(getCurrentHourRange());
  }, [sweepsSummary]);

  return (
    <>
      <div className={styles.range}>({timeRangeString})</div>
      <div className={styles.divider}></div>
      <div className={styles.sweepTitle}>
        <Trans i18nKey={'dashboardNew.obligatorySweeps'} />{' '}
        <span>
          {hourlySweepData &&
            hourlySweepData.obligatory.filter(
              (item) => item.status === HourlySweepStatus.COMPLETED,
            ).length}
          /{hourlySweepData && hourlySweepData.obligatory.length}
        </span>
      </div>
      <div className={styles.eyeCardsContainer}>
        {hourlySweepData &&
          hourlySweepData.obligatory.map((item, index) => (
            <EyeCard key={index} data={item} type={SweepType.Obligatory} />
          ))}
      </div>
      <div className={styles.divider}></div>
      {hourlySweepData && hourlySweepData.voluntary.length > 0 && (
        <>
          <div className={styles.sweepTitle}>
            <Trans i18nKey={'dashboardNew.voluntarySweeps'} />
            <span>{hourlySweepData.voluntary.length}</span>
          </div>
          <div className={styles.eyeCardsContainer}>
            {hourlySweepData.voluntary.map((item, index) => (
              <EyeCard key={index} data={item} type={SweepType.Voluntary} />
            ))}
          </div>
          <div className={styles.divider}></div>
        </>
      )}

      <div className={styles.summaryStats}>
        {DEFAULT_HOURLY_SWEEP_MACROS.map((macro, index) => (
          <div key={index} className={styles.statItem}>
            <div className={styles.label}>
              <div
                className={`${styles.dot} h-[10px]`}
                style={{ backgroundColor: macro.color }}
              />
              <Trans i18nKey={macro.title} />
            </div>
            <div className={styles.value}>
              {hourlySweepData
                ? macro.type === SweepType.Voluntary
                  ? hourlySweepData.voluntary.length
                  : macro.status === HourlySweepStatus.COMPLETED
                    ? hourlySweepData.obligatory.filter(
                        (item) => item.status === HourlySweepStatus.COMPLETED,
                      ).length
                    : macro.status === HourlySweepStatus.MISSED
                      ? hourlySweepData.obligatory.filter(
                          (item) => item.status === HourlySweepStatus.MISSED,
                        ).length
                      : hourlySweepData.obligatory.filter(
                          (item) => item.status === HourlySweepStatus.PENDING,
                        ).length
                : 0}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export const processSweepsSummary = (
  sweepsSummary: Sweep[],
  venueBranchInterval: number,
): HourlySweepData => {
  const result: HourlySweepData = {
    obligatory: [],
    voluntary: [],
  };

  const baseMoment: Moment =
    sweepsSummary.length > 0
      ? moment(sweepsSummary[0].createdOn).startOf('hour')
      : moment().startOf('hour');

  const obligatorySlots: Moment[] = Array.from(
    { length: 60 / venueBranchInterval },
    (_, i) => baseMoment.clone().minutes(i * venueBranchInterval),
  );

  const obligatorySweeps: Sweep[] = sweepsSummary.filter(
    (sweep: Sweep) => sweep.type === SweepType.Obligatory,
  );

  const obligatoryMap: Map<string, Sweep> = new Map(
    obligatorySweeps.map((sweep: Sweep): [string, Sweep] => {
      const schedMoment: Moment = sweep.scheduledTime
        ? moment(sweep.scheduledTime)
        : moment(sweep.createdOn)
            .startOf('hour')
            .add(
              Math.floor(
                moment(sweep.createdOn).diff(baseMoment, 'minutes') /
                  venueBranchInterval,
              ) * venueBranchInterval,
              'minutes',
            );

      const sweepScheduleKey: string = schedMoment.format('HH:mm');
      return [sweepScheduleKey, sweep];
    }),
  );

  obligatorySlots.forEach((slot: Moment) => {
    const scheduleKey: string = slot.format('HH:mm');
    const existingSweep: Sweep | undefined = obligatoryMap.get(scheduleKey);

    result.obligatory.push({
      sweepTime: existingSweep
        ? moment(existingSweep.createdOn).toDate()
        : undefined,
      status: existingSweep
        ? existingSweep.status === SweepStatus.Completed
          ? HourlySweepStatus.COMPLETED
          : HourlySweepStatus.MISSED
        : HourlySweepStatus.PENDING,
      sweepSchedule: scheduleKey,
    });
  });

  sweepsSummary.forEach((sweep: Sweep) => {
    if (sweep.type === SweepType.Voluntary) {
      result.voluntary.push({
        sweepTime: moment(sweep.createdOn).toDate(),
        status:
          sweep.status === SweepStatus.Completed
            ? HourlySweepStatus.COMPLETED
            : sweep.status === SweepStatus.Missed
              ? HourlySweepStatus.MISSED
              : HourlySweepStatus.PENDING,
      });
    }
  });

  return result;
};
